import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, useAuth, useToast } from "@ozpanel/ui";

// ImageVersion — the version a container app is PINNED to, and the version each node holds.
//
// An app's image never changes by itself (image_pin.go): every node runs exactly the pinned digest. This
// is where a person changes it: "Check for updates" asks the registry what the tag names now (nothing is
// downloaded), and "Update" pins that version for every node. One component for the tenant's console and
// the operator's — the server decides who may call it.

type ImageState = {
  image: string;
  pinned: string;
  auto_update: boolean;
  nodes?: { node: string; held: string; running: boolean }[] | null;
};
type CheckResult = { pinned: string; available: string; update_available: boolean; updated?: boolean };

const short = (d?: string) => (d ? d.replace(/^sha256:/, "").slice(0, 12) : "—");

export function ImageVersion({ appName, onChanged }: { appName: string; onChanged?: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const name = encodeURIComponent(appName);
  const q = useQuery({
    queryKey: ["app-image", appName],
    queryFn: () => client.apiFetch<ImageState>(`/apps/${name}/image`),
  });
  const [busy, setBusy] = React.useState(false);
  const [found, setFound] = React.useState<CheckResult | null>(null);
  const [confirm, setConfirm] = React.useState(false);

  const fail = (title: string) => (e: unknown) =>
    notify({ kind: "error", title, body: e instanceof Error ? e.message : String(e) });

  const check = async () => {
    setBusy(true);
    try {
      const r = await client.apiFetch<CheckResult>(`/apps/${name}/image`, { method: "POST", body: JSON.stringify({ apply: false }) });
      setFound(r);
      if (!r.update_available) notify({ kind: "success", title: "Already on the version the registry has" });
    } catch (e) {
      fail("Could not ask the registry")(e);
    } finally {
      setBusy(false);
    }
  };

  const update = async () => {
    setBusy(true);
    try {
      const r = await client.apiFetch<CheckResult>(`/apps/${name}/image`, { method: "POST", body: JSON.stringify({ apply: true }) });
      setFound(null);
      notify({ kind: "success", title: r.updated ? `Pinned ${short(r.pinned)} — every node moves to it` : "Nothing to update" });
      void qc.invalidateQueries({ queryKey: ["app-image", appName] });
      onChanged?.();
    } catch (e) {
      fail("Could not update")(e);
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const d = q.data;
  if (!d) return null;
  const nodes = d.nodes ?? [];
  return (
    <div className="rounded border border-line p-3 text-[12.5px]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-muted">Pinned version</span>
        <span className="font-mono">{d.pinned ? short(d.pinned) : "not pinned yet (pins at first start)"}</span>
        <span className="text-muted">· automatic updates {d.auto_update ? "on" : "off"}</span>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void check()}>Check for updates</Button>
          {found?.update_available && (
            <Button size="sm" disabled={busy} onClick={() => setConfirm(true)}>Update to {short(found.available)}</Button>
          )}
        </span>
      </div>
      {nodes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
          {nodes.map((n) => {
            const differs = !!d.pinned && !!n.held && n.held !== d.pinned;
            return (
              <span key={n.node} className={differs ? "text-warn" : undefined}>
                {n.node}: <span className="font-mono">{n.held ? short(n.held) : "not held"}</span>
                {n.running ? " · running" : ""}
                {differs ? " · differs from the pin" : ""}
              </span>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={confirm}
        title={`Update ${appName}?`}
        body={`Every node that runs it restarts it on version ${short(found?.available)} (now ${short(d.pinned)}).`}
        confirmLabel="Update"
        pending={busy}
        onOpenChange={(o) => !o && setConfirm(false)}
        onConfirm={() => void update()}
      />
    </div>
  );
}
